import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import * as React from "react";
import { FieldValues, UseControllerProps, useWatch } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { addWeeks, format } from "date-fns";

import AutocompleteFormField from "components/autocomplete-form-field";
import { APPOINTMENT_STATUSES } from "constants/appointments";
import { DISPLAY_SUPERVISING_PROVIDER } from "constants/feature-flags";
import { useGetPatientAppointmentsQuery } from "services/appointments";
import type { TinyAppointment } from "services/types/appointments.type";

import { convertDateInTimezoneToCurrent } from "../../utils/common";
import {
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_ICONS
} from "../appointment-status-cell/appointment-status-cell.component";
import StatusIconContainer from "../status-transition-cell/status-icon-container.component";

type AppointmentFormFieldProps = {
  label?: string;
  disabled?: boolean;
  required?: boolean;
  patientId?: string;
  appointmentTypeGeneralType?: number;
  limitToWeekFromNow?: boolean;
  showStatus?: boolean;
  status?: number[];
  sx?: SxProps;
  disableClearable?: boolean;
  inputSx?: SxProps;
  onAppointmentSelected?: any;
  showSelectedAppointmentType?: boolean;
  /**
   * Only appointments that start from now on, soonest first — with `limit`, the patient's next few.
   * "Now" is taken when the field mounts, so the request stays the same across renders.
   */
  upcomingOnly?: boolean;
  /** How many appointments to fetch. */
  limit?: number;
  /**
   * Called with each response for the CURRENT patient. RTK Query keeps serving the previous
   * patient's list while the next one loads, so the field's options can briefly be theirs; this
   * never is.
   */
  onAppointmentsLoaded?: (appointments: TinyAppointment[]) => void;
  /**
   * RestQL projection for the fetch. Omitted, the field asks for the wide one its first callers
   * need (room, chart note, diagnosis, noah ids). A caller that shows only the options can ask
   * for less: it must still include what the field itself renders, i.e. `start_moment`,
   * `status`, `staff_member` (tooltip), `clinic{timezone}` and `type{name}`.
   */
  query?: string;
};

export type RhfAppointmentFormFieldProps<T extends FieldValues> =
  AppointmentFormFieldProps & UseControllerProps<T>;

/**
 * " (Confirmed)" — or nothing when the status is unknown or the option has no status at all. An
 * appointment reached through a report row may carry no `status`, and `getFromCode` returns "" for
 * codes outside the enum; either used to print a bare "()" after the type name.
 */
const statusSuffix = (option: TinyAppointment, showStatus: boolean) => {
  if (!showStatus) return "";
  const label = APPOINTMENT_STATUSES.getFromCode(option.status);
  return label ? ` (${label})` : "";
};

export default function AppointmentFormField<T extends FieldValues>({
  control,
  name,
  patientId = "",
  label = "Appointment",
  disableClearable = false,
  required = false,
  disabled = false,
  limitToWeekFromNow = false,
  showStatus = false,
  appointmentTypeGeneralType,
  status,
  inputSx = {},
  onAppointmentSelected,
  showSelectedAppointmentType,
  upcomingOnly = false,
  limit = 300,
  onAppointmentsLoaded,
  query,
  sx = {}
}: RhfAppointmentFormFieldProps<T>) {
  const flags = useFlags();
  const displaySupervisingProvider = flags[DISPLAY_SUPERVISING_PROVIDER];

  const [weekFromNow] = useState<string | undefined>(
    limitToWeekFromNow ? addWeeks(new Date(), 1).toISOString() : undefined
  );
  const [fromNow] = useState<string | undefined>(
    upcomingOnly ? new Date().toISOString() : undefined
  );
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: TinyAppointment) =>
      format(
        new Date(
          convertDateInTimezoneToCurrent(
            option.startDate,
            option?.clinic?.timezone
          ).toISOString()
        ),
        "EEE MMM dd, yyyy hh:mm aaa"
      ) +
      " " +
      option.appointmentType.name +
      statusSuffix(option, showStatus)
  });
  const selectedOption = useWatch({ control, name }) as TinyAppointment;
  const {
    data: appointments = [],
    currentData,
    isLoading,
    isSuccess
  } = useGetPatientAppointmentsQuery(
    {
      patientId: Number.parseInt(patientId),
      appointment_type_general_type: appointmentTypeGeneralType,
      fromDate: fromNow,
      upToDate: weekFromNow,
      // Undefined keeps the endpoint's newest-first default.
      ordering: upcomingOnly ? "start_moment" : undefined,
      baseFilter: status ? { status } : undefined,
      limit,
      query:
        query ??
        (displaySupervisingProvider
          ? "{id,start_moment,end_moment,status,staff_member{id,first_name,last_name,suffix,title,is_active},is_direct_supervisor,supervising_provider{id,first_name,last_name,suffix,title,is_active},room{clinic{id,name,logo,state,practice{id,name,logo}}},patient{id,first_name,last_name,noah_id,noah_patient_id},clinic{id,name,logo,state,practice{id,name,logo},timezone},type{id,name,default_duration},diagnosis,chart_note{id,icd10_codes}}"
          : "{id,start_moment,end_moment,status,staff_member{id,first_name,last_name,suffix,title,is_active},room{clinic{id,name,logo,state,practice{id,name,logo}}},patient{id,first_name,last_name,noah_id,noah_patient_id},clinic{id,name,logo,state,practice{id,name,logo},timezone},type{id,name,default_duration},diagnosis,chart_note{id,icd10_codes}}")
    },
    { skip: !patientId }
  );

  useEffect(() => {
    if (currentData) onAppointmentsLoaded?.(currentData as TinyAppointment[]);
  }, [currentData, onAppointmentsLoaded]);

  if (!isSuccess) {
    return <></>;
  }

  return (
    <Tooltip
      disableInteractive
      placement="top-start"
      title={
        selectedOption ? (
          <Stack rowGap={0.5}>
            <Typography variant="body1" fontWeight={700}>
              {format(
                new Date(selectedOption.startDate),
                "EEE MMM dd, yyyy hh:mm aaa"
              )}
            </Typography>
            <Typography variant="body1">
              {selectedOption.appointmentType?.name}
            </Typography>
            <Typography variant="body1">
              {APPOINTMENT_STATUSES.getFromCode(selectedOption.status)}
            </Typography>
            <Typography variant="body1">
              {selectedOption.staffMember?.fullName}
            </Typography>
          </Stack>
        ) : undefined
      }
    >
      <Box width="100%">
        <AutocompleteFormField
          name={name}
          label={label}
          disabled={disabled}
          disableClearable={disableClearable}
          required={required}
          options={appointments as TinyAppointment[]}
          isLoading={isLoading}
          filterOptions={filterOptions}
          onOptionSelected={onAppointmentSelected}
          getOptionLabel={(option: TinyAppointment) =>
            `${format(
              new Date(option.startDate),
              "EEE MMM dd, yyyy hh:mm aaa"
            )}${
              showSelectedAppointmentType
                ? " (" + option.appointmentType?.name + ")"
                : ""
            }`
          }
          renderOption={(props: object, option: TinyAppointment) => (
            <ListItem {...props}>
              <Stack direction="row" gap={1}>
                {showStatus && (
                  <StatusIconContainer
                    color={APPOINTMENT_STATUS_COLORS[option.status]}
                  >
                    {APPOINTMENT_STATUS_ICONS[option.status]}
                  </StatusIconContainer>
                )}
                <ListItemText
                  secondary={`${option.appointmentType.name}${statusSuffix(
                    option,
                    showStatus
                  )}`}
                >
                  {format(
                    new Date(option.startDate),
                    "EEE MMM dd, yyyy hh:mm aaa"
                  )}
                </ListItemText>
              </Stack>
            </ListItem>
          )}
          isOptionEqualToValue={(
            option: TinyAppointment,
            value: TinyAppointment
          ) => option.id === value.id}
          popupIcon={<ArrowDropDownIcon />}
          control={control}
          sx={sx}
          inputSx={inputSx}
        />
      </Box>
    </Tooltip>
  );
}
